import { useCallback, useEffect, useRef, useState } from 'react'
import s from './CustomSelect.module.css'

interface Option {
  value: string
  label: string
}

interface CustomSelectProps {
  options: Option[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  label?: string
  error?: string
  filterOption?: (candidate: Option, input: string) => boolean
  isSearchable?: boolean
  onInputChange?: (value: string) => void
}

export default function CustomSelect({
  value,
  options,
  onChange,
  placeholder = 'Оберіть опцію',
  className = '',
  label,
  error,
  filterOption,
  isSearchable = false,
  onInputChange,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [searchInput, setSearchInput] = useState('')
  const selectRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.value === value)

  // Фільтруємо опції на основі пошуку
  const filteredOptions =
    isSearchable && filterOption
      ? options.filter(option => filterOption(option, searchInput))
      : options

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue)
      setIsOpen(false)
      setHighlightedIndex(-1)
    },
    [onChange],
  )

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setHighlightedIndex(-1)
      setSearchInput('')
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setIsOpen(false)
      }
    }

    const handleArrowKeys = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
      } else if (event.key === 'Enter' && highlightedIndex >= 0) {
        event.preventDefault()
        handleSelect(filteredOptions[highlightedIndex].value)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
      document.addEventListener('keydown', handleTabKey)
      document.addEventListener('keydown', handleArrowKeys)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
      document.removeEventListener('keydown', handleTabKey)
      document.removeEventListener('keydown', handleArrowKeys)
    }
  }, [isOpen, handleSelect, highlightedIndex, filteredOptions, isSearchable])

  return (
    <div className={`${s.customSelectContainer} ${className}`}>
      <div className={`${s.customSelect} ${error ? s.error : ''}`} ref={selectRef}>
        <div
          className={`${s.selectHeader} ${isOpen ? s.open : ''} ${error ? s.errorHeader : ''}`}
          onClick={handleToggle}
        >
          <span className={s.selectedValue}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
          >
            <path
              d="M14 1.00024V1.00024C10.1372 1.00024 7.00092 4.13718 7.01128 7.99999V7.99999"
              stroke="#0D0D0D"
              strokeWidth="1.5"
            />
            <path
              d="M0 1V1C3.86283 1 6.99908 4.13693 6.98872 7.99974V7.99974"
              stroke="#0D0D0D"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        {isOpen && (
          <div className={s.dropdown}>
            {isSearchable && (
              <div className={s.searchInput}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => {
                    setSearchInput(e.target.value)
                    if (onInputChange) onInputChange(e.target.value)
                  }}
                  onKeyDown={e => {
                    // Запобігаємо всплиттю подій до батьківських обробників
                    e.stopPropagation()
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault()
                      // Передаємо фокус на список опцій
                      setHighlightedIndex(0)
                    }
                  }}
                  placeholder="Почніть вводити назву..."
                  autoFocus
                />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`${s.option} ${option.value === value ? s.selected : ''} ${
                    index === highlightedIndex ? s.highlighted : ''
                  }`}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className={s.noOptions}>Нічого не знайдено</div>
            )}
          </div>
        )}
      </div>
      {label && (
        <div className={s.labelContainer}>
          <label className={s.fieldLabel}>{label}</label>
        </div>
      )}
      {error && <div className={s.errorMessage}>{error}</div>}
    </div>
  )
}
